/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {clearAllToasts} from '../../../utils/toast/Toast'

const loginSchema = Yup.object().shape({
    username: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('User Name is required'),
    password: Yup.string()
        .required('Password is required'),
});

const initialValues = {
    username: '',
    password: '',
};

const rotatingContent = [
    {
        icon: 'ki-chart-line-up-2',
        title: 'AI-Driven Revenue Intelligence',
        description: 'Leverage artificial intelligence to identify VAS revenue opportunities and maximize profitability',
        color: 'primary',
    },
    {
        icon: 'ki-technology-4',
        title: 'Real-Time Operational Visibility',
        description: 'Track attendance, treatments, and material consumption with live reporting and analytics',
        color: 'success',
    },
    {
        icon: 'ki-security-check',
        title: 'Automated Incentive Calculations',
        description: 'Eliminate manual errors and incentive leakage with intelligent automation systems',
        color: 'warning',
    },
    {
        icon: 'ki-graph-up',
        title: 'Performance Analytics Dashboard',
        description: 'Advanced reports and data-driven insights for service center performance optimization',
        color: 'info',
    },
    {
        icon: 'ki-cloud',
        title: 'Scalable Cloud Platform',
        description: 'Industry-specific workflows designed for growing service centers and VAS vendors',
        color: 'danger',
    },
];

export function Login() {
    const [loading, setLoading] = useState(false);
    const [currentContentIndex, setCurrentContentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const {saveAuth, setCurrentUser} = useAuth();

    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentContentIndex((prev) => (prev + 1) % rotatingContent.length);
                setIsTransitioning(false);
            }, 300); // Half of transition duration
        }, 5000); // Change every 5 seconds

        return () => clearInterval(interval);
    }, []);

    const formik = useFormik({
        initialValues,
        validationSchema: loginSchema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true);
            try {
                const {data: auth} = await login(values.username, values.password);
                // Clear all error toasts from previous failed login attempts
                clearAllToasts();
                saveAuth(auth);
                const {data: user} = await getUserByToken(auth.accessToken);
                setCurrentUser(user)
            } catch (error: any) {
                console.error(error);
                saveAuth(undefined);
                const message = error?.response?.data?.message || error?.response?.data || 'The login details are incorrect';
                setStatus(message);
                setSubmitting(false);
                setLoading(false)
            }
        },
    });

    return (
        <form
            className='form w-100'
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
        >
            {/* begin::Heading */}
            <div className='text-center mb-11'>
                <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
                <div className='text-gray-500 fw-semibold fs-6'>
                    Simplifying Operations. Maximizing VAS Revenue.
                </div>
            </div>
            {/* end::Heading */}

            {formik.status ? (
                <div className='mb-lg-15 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
            ) : (
                <div
                    className={`mb-10 bg-light-${
                        rotatingContent[currentContentIndex].color
                    } p-6 rounded-3 border border-${
                        rotatingContent[currentContentIndex].color
                    } border-dashed transition-all duration-600 ${
                        isTransitioning
                            ? 'opacity-50 transform scale-95'
                            : 'opacity-100 transform scale-100'
                    }`}
                    style={{
                        transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        minHeight: '120px',
                    }}
                >
                    <div className='d-flex align-items-center h-100'>
                        <div className='symbol symbol-40px me-3'>
                            <div
                                className={`symbol-label bg-${rotatingContent[currentContentIndex].color}`}
                            >
                                <i
                                    className={`ki-duotone ${rotatingContent[currentContentIndex].icon} fs-2 text-white`}
                                >
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                    <span className='path3'></span>
                                </i>
                            </div>
                        </div>
                        <div className='flex-grow-1'>
                            <div
                                className={`text-${rotatingContent[currentContentIndex].color} fw-bold fs-6 mb-1`}
                            >
                                {rotatingContent[currentContentIndex].title}
                            </div>
                            <div className='text-gray-700 fs-7'>
                                {rotatingContent[currentContentIndex].description}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* begin::Form group */}
            <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>User Name</label>
                <input
                    placeholder=''
                    {...formik.getFieldProps('username')}
                    className={clsx(
                        'form-control bg-transparent',
                        {'is-invalid': formik.touched.username && formik.errors.username},
                        {
                            'is-valid': formik.touched.username && !formik.errors.username,
                        }
                    )}
                    type='username'
                    name='username'
                    autoComplete='off'
                />
                {formik.touched.username && formik.errors.username && (
                    <div className='fv-plugins-message-container'>
                        <span role='alert'>{formik.errors.username}</span>
                    </div>
                )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className='fv-row mb-3'>
                <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                <input
                    type='password'
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                        'form-control bg-transparent',
                        {
                            'is-invalid': formik.touched.password && formik.errors.password,
                        },
                        {
                            'is-valid': formik.touched.password && !formik.errors.password,
                        }
                    )}
                />
                {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.password}</span>
                        </div>
                    </div>
                )}
            </div>
            {/* end::Form group */}

            {/* begin::Wrapper */}
            <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
                <div/>

                {/* begin::Link */}
                <Link to='/auth/forgot-password' className='link-primary'>
                    Forgot Password ?
                </Link>
                {/* end::Link */}
            </div>
            {/* end::Wrapper */}

            {/* begin::Action */}
            <div className='d-grid mb-10'>
                <button
                    type='submit'
                    id='kt_sign_in_submit'
                    className='btn btn-primary'
                    disabled={formik.isSubmitting || !formik.isValid}
                >
                    {!loading && <span className='indicator-label'>Continue</span>}
                    {loading && (
                        <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
                    )}
                </button>
            </div>
            {/* end::Action */}

            {/* begin::Separator */}
            <div className='separator separator-content my-14'>
                <span className='w-125px text-gray-500 fw-semibold fs-7'>OR</span>
            </div>
            {/* end::Separator */}

            <div className='text-gray-500 text-center fw-semibold fs-6'>
                <i className='ki-duotone ki-rocket fs-5 me-2'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                </i>
                Want to boost your VAS revenue?{' '}
                <Link to='/auth/request-demo' className='link-primary text-hover-primary'>
                    Request Demo
                </Link>
            </div>
        </form>
    )
}
